import { memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { GoogleMap } from '../../../../../components';
import { ProfileMapProps } from './ProfileMap.types';

const MemoizedGoogleMap = memo(
  ({
    latitude,
    longitude,
    stationName,
    stationAddress,
    brand
  }: {
    latitude: number;
    longitude: number;
    stationName: string;
    stationAddress: string;
    brand: string;
  }): JSX.Element => (
    <GoogleMap
      latitude={latitude}
      longitude={longitude}
      stationName={stationName}
      stationAddress={stationAddress}
      brand={brand}
      zoom={16}
    />
  )
);

/**
 * Profile map component displaying station location on Google Maps
 * Shows interactive map with station marker when valid coordinates are available
 *
 * Features:
 * - Google Maps integration with custom markers
 * - Coordinate validation and error handling
 * - Station-specific marker styling
 * - Automatic map centering and zoom
 * - Fallback handling for invalid coordinates
 *

 */
export function ProfileMap({ stationData }: ProfileMapProps): JSX.Element | null {
  if (!stationData) {
    return <></>;
  }

  const mapProps = useMemo(() => {
    const lat = parseFloat(String(stationData.latitude));
    const lng = parseFloat(String(stationData.longitude));

    if (!stationData.latitude || !stationData.longitude || isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
      return null;
    }

    return {
      latitude: lat,
      longitude: lng,
      stationName: stationData.station_number,
      stationAddress: stationData.address || '',
      brand: stationData.brand || ''
    };
  }, [
    stationData.latitude,
    stationData.longitude,
    stationData.station_number,
    stationData.unicode,
    stationData.address,
    stationData.brand
  ]);

  if (!mapProps) return null;

  return (
    <Box className="station-profile-maps-section">
      <Typography className="station-profile-maps-title">Розташування на карті:</Typography>
      <Box className="station-profile-maps-container">
        <MemoizedGoogleMap {...mapProps} />
      </Box>
    </Box>
  );
}
